import React, { useState } from 'react';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Button from '@atlaskit/button/standard-button';
import { N500A } from '@atlaskit/theme/colors';
import { Promotion, Maybe } from '@css-xp/graphtypes';
import { Promotion as PromotionV2 } from '@css-xp/graphtypes/src/generated-types-new';
import { getCustomPromotionReason, getPromotionTitle, renderPromotionValue } from '@css-xp/base';
import './active-promotion-section.scss';
import { PartnerPromotionItem } from '@css-xp/base/partner-promotion-item';
import { PARTNER_DISCOUNT } from '@atlassian/cssxp-utils-entitlement';

type ActivePromotion = Promotion & { isRemoved?: boolean };

const ActivePromotionBox = ({
  promotion,
  index,
  updatePromotionsToRemove,
  enableIsRemoved,
}: {
  promotion: Promotion | undefined | null;
  index: number;
  updatePromotionsToRemove: (id: string, index: string, isRemoved: boolean, promotionType: string) => void;
  enableIsRemoved?: boolean | undefined;
}) => {
  const [isRemoved, setIsRemoved] = useState<boolean>(enableIsRemoved ?? false);
  const handleRemove = () => {
    setIsRemoved((prevIsRemoved) => {
      updatePromotionsToRemove(promotion?.id as string, index.toString(), !prevIsRemoved, promotion?.type as string);
      return !prevIsRemoved;
    });
  };

  const isPartnerDiscount = promotion?.type === PARTNER_DISCOUNT;

  return (
    <div
      className={`active-promotion-box mt-8${isRemoved || enableIsRemoved ? ' removed' : ''}`}
      data-testid={`index-${index}`}
    >
      <h4 className="mb-8">
        <span className="mr-4">{getPromotionTitle(promotion, { handleEmptyTitle: true })}</span>
        {promotion?.type === PARTNER_DISCOUNT && promotion?.applicationReason?.readableName && (
          <Tooltip content={getCustomPromotionReason(promotion)} tag={'span'}>
            <InfoIcon size="small" testId="info-icon" label="Info" primaryColor={N500A} />
          </Tooltip>
        )}
      </h4>
      <div className="remove-promotion-link-container">
        <p>{renderPromotionValue(promotion, { showDuration: true, showReason: true })}</p>
        {enableIsRemoved || (
          <div className="remove-promotion-button">
            <Button
              appearance="link"
              testId={`remove-promotion-${promotion?.id ?? index}`}
              onClick={() => handleRemove()}
              isDisabled={isPartnerDiscount} // cannot remove partner discount
            >
              {isRemoved ? 'Undo removal' : 'Remove'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ActivePromotionSection = ({
  promotions,
  updatePromotionsToRemove,
  heading,
  tooltip,
  enableIsRemoved,
  partnerPromotion,
}: {
  promotions: ActivePromotion[] | null | undefined;
  updatePromotionsToRemove: (promotionId: string, index: string, isRemoved: boolean, promotionType: string) => void;
  heading?: string | undefined | null;
  tooltip?: string | undefined | null;
  enableIsRemoved?: boolean | undefined;
  partnerPromotion?: PromotionV2 | null;
}) => {
  if (!promotions?.length && !partnerPromotion) {
    return null;
  }

  return (
    <div className="active-promotion-section">
      <h3>
        <span className="mr-4 active-promotion-heading">{heading ? heading : 'Current discounts'}</span>
        {tooltip && (
          <Tooltip content={tooltip} tag="span">
            <InfoIcon size="small" label="active-promotion-info" />
          </Tooltip>
        )}
      </h3>
      {partnerPromotion && <PartnerPromotionItem partnerPromotion={partnerPromotion} />}
      {(promotions ?? []).map((promotion: ActivePromotion | undefined | null, index: number) => {
        return (
          <ActivePromotionBox
            promotion={promotion}
            key={promotion?.id}
            index={index}
            updatePromotionsToRemove={updatePromotionsToRemove}
            enableIsRemoved={enableIsRemoved || Boolean(promotion?.isRemoved)}
          />
        );
      })}
    </div>
  );
};
